import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, MapPin, Truck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import { FEE_CONFIG_ERRORS, getFeeConfig, updateFeeConfig } from '../../api/deliveryFees';
import { SLOT_CONFIG_ERRORS, getSlotConfig, updateSlotConfig } from '../../api/deliverySlots';
import form from '../../styles/form.module.css';
import styles from './DeliveryFees.module.css';

export default function DeliveryFees() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  const [testCart, setTestCart] = useState(350);

  const [slotConfig, setSlotConfig] = useState(null);
  const [slotDraft, setSlotDraft] = useState(null);
  const [slotLoading, setSlotLoading] = useState(true);
  const [slotLoadError, setSlotLoadError] = useState('');
  const [slotSaving, setSlotSaving] = useState(false);
  const [slotSaveError, setSlotSaveError] = useState('');
  const [slotJustSaved, setSlotJustSaved] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const cfg = await getFeeConfig(user.idToken);
      setConfig(cfg);
      setDraft(cfg);
    } catch (err) {
      setLoadError(err.message || 'Could not load delivery fee configuration.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  const loadSlotConfig = useCallback(async () => {
    setSlotLoading(true);
    setSlotLoadError('');
    try {
      const cfg = await getSlotConfig(user.idToken);
      setSlotConfig(cfg);
      setSlotDraft(cfg);
    } catch (err) {
      setSlotLoadError(err.message || 'Could not load delivery slot settings.');
    } finally {
      setSlotLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    loadConfig();
    loadSlotConfig();
  }, [loadConfig, loadSlotConfig]);

  const setSlot = (key) => (e) => setSlotDraft((d) => ({ ...d, [key]: Number(e.target.value) }));
  const slotDirty = !!slotConfig && !!slotDraft && JSON.stringify(slotConfig) !== JSON.stringify(slotDraft);

  const handleSlotSave = async (e) => {
    e.preventDefault();
    if (!slotDirty || slotSaving) return;
    setSlotSaving(true);
    setSlotSaveError('');
    try {
      const cfg = await updateSlotConfig(slotDraft, user.idToken);
      setSlotConfig(cfg);
      setSlotDraft(cfg);
      setSlotJustSaved(true);
      setTimeout(() => setSlotJustSaved(false), 2200);
    } catch (err) {
      setSlotSaveError(SLOT_CONFIG_ERRORS[err.message] || err.message || 'Could not save delivery slot settings.');
    } finally {
      setSlotSaving(false);
    }
  };

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: Number(e.target.value) }));
  const dirty = !!config && !!draft && JSON.stringify(config) !== JSON.stringify(draft);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const cfg = await updateFeeConfig(draft, user.idToken);
      setConfig(cfg);
      setDraft(cfg);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    } catch (err) {
      setSaveError(FEE_CONFIG_ERRORS[err.message] || err.message || 'Could not save delivery fee configuration.');
    } finally {
      setSaving(false);
    }
  };

  const computedFee = useMemo(
    () => (draft && testCart >= draft.freeDeliveryThreshold ? 0 : draft?.baseFee ?? 0),
    [testCart, draft],
  );
  const remaining = draft ? Math.max(0, draft.freeDeliveryThreshold - testCart) : 0;
  const progressPct = draft ? Math.min(100, Math.round((testCart / (draft.freeDeliveryThreshold || 1)) * 100)) : 0;

  return (
    <div>
      <PageHeader
        eyebrow="Delivery"
        title="Fee Configuration"
        subtitle="Set what customers pay for delivery, and the cart size that unlocks free delivery."
      />

      {loading && <p className={styles.hint}>Loading delivery fee configuration…</p>}

      {!loading && loadError && (
        <div className={styles.hint}>
          <p className={styles.errorText} style={{ margin: 0 }}>
            <AlertTriangle size={12} /> {loadError}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={loadConfig} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </div>
      )}

      {!loading && !loadError && draft && (
        <div className={styles.layout}>
          <form className={styles.formCard} onSubmit={handleSave}>
            <label className={form.field}>
              <span className={form.label}>Base delivery fee</span>
              <div className={form.prefixInput}>
                <span>₹</span>
                <input className={form.input} type="number" min="0" value={draft.baseFee} onChange={set('baseFee')} />
              </div>
              <span className={form.hint}>Charged on every order below the free delivery threshold.</span>
            </label>

            <label className={form.field}>
              <span className={form.label}>Free delivery threshold</span>
              <div className={form.prefixInput}>
                <span>₹</span>
                <input className={form.input} type="number" min="0" value={draft.freeDeliveryThreshold} onChange={set('freeDeliveryThreshold')} />
              </div>
              <span className={form.hint}>Orders at or above this cart value ship free.</span>
            </label>

            <div className={form.row}>
              <label className={form.field}>
                <span className={form.label}>Per-km fee</span>
                <div className={form.prefixInput}>
                  <span>₹</span>
                  <input className={form.input} type="number" min="0" value={draft.perKmFee} onChange={set('perKmFee')} />
                </div>
                <span className={form.hint}>Stored for later use — not yet applied to the fee calculation.</span>
              </label>
              <label className={form.field}>
                <span className={form.label}>Max delivery radius (km)</span>
                <input className={form.input} type="number" min="0" value={draft.maxDeliveryRadiusKm} onChange={set('maxDeliveryRadiusKm')} />
              </label>
            </div>

            {saveError && (
              <p className={styles.errorText}>
                <AlertTriangle size={12} /> {saveError}
              </p>
            )}

            <Button type="submit" disabled={!dirty || saving} className={styles.saveBtn}>
              {saving ? 'Saving…' : justSaved ? <><CheckCircle2 size={15} /> Saved</> : 'Save changes'}
            </Button>
          </form>

          <div className={styles.previewCard}>
            <p className={styles.previewLabel}>Live preview</p>
            <div className={styles.previewIconWrap}><Truck size={20} /></div>

            <p className={styles.previewHeadline}>
              {computedFee === 0 ? 'Free delivery' : `${formatCurrency(computedFee)} delivery fee`}
            </p>
            <p className={styles.previewSub}>
              {computedFee === 0
                ? 'This order qualifies for free delivery.'
                : `Add ${formatCurrency(remaining)} more to unlock free delivery.`}
            </p>

            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>

            <label className={styles.sliderLabel}>
              <span>Test cart value</span>
              <span className={styles.sliderValue}>{formatCurrency(testCart)}</span>
            </label>
            <input
              type="range"
              min="0"
              max={Math.max(draft.freeDeliveryThreshold * 2, 200)}
              step="10"
              value={testCart}
              onChange={(e) => setTestCart(Number(e.target.value))}
              className={styles.slider}
            />

            <div className={styles.radiusNote}>
              <MapPin size={13} /> Max radius {draft.maxDeliveryRadiusKm}km · ₹{draft.perKmFee}/km
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 32 }}>
        <PageHeader
          eyebrow="Delivery"
          title="Schedule Settings"
          subtitle="Control how far ahead customers can book a slot, when scheduled cancels get cut off, slot capacity, and the instant-order ETA."
        />
      </div>

      {slotLoading && <p className={styles.hint}>Loading delivery slot settings…</p>}

      {!slotLoading && slotLoadError && (
        <div className={styles.hint}>
          <p className={styles.errorText} style={{ margin: 0 }}>
            <AlertTriangle size={12} /> {slotLoadError}
          </p>
          <Button type="button" variant="outline" size="sm" onClick={loadSlotConfig} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </div>
      )}

      {!slotLoading && !slotLoadError && slotDraft && (
        <form className={styles.formCard} style={{ maxWidth: 420 }} onSubmit={handleSlotSave}>
          <div className={form.row}>
            <label className={form.field}>
              <span className={form.label}>Slot horizon (days)</span>
              <input className={form.input} type="number" min="0" value={slotDraft.slotHorizonDays} onChange={setSlot('slotHorizonDays')} />
              <span className={form.hint}>How many days ahead customers can pick a scheduled slot.</span>
            </label>
            <label className={form.field}>
              <span className={form.label}>Cancel cutoff (minutes)</span>
              <input className={form.input} type="number" min="0" value={slotDraft.cancelCutoffMinutes} onChange={setSlot('cancelCutoffMinutes')} />
              <span className={form.hint}>Scheduled orders can't cancel within this window of slot start.</span>
            </label>
          </div>

          <div className={form.row}>
            <label className={form.field}>
              <span className={form.label}>Max orders per slot</span>
              <input className={form.input} type="number" min="0" value={slotDraft.maxOrdersPerSlot} onChange={setSlot('maxOrdersPerSlot')} />
              <span className={form.hint}>Caps how many scheduled orders one hour slot can take.</span>
            </label>
            <label className={form.field}>
              <span className={form.label}>Instant ETA (minutes)</span>
              <input className={form.input} type="number" min="0" value={slotDraft.instantEtaMinutes} onChange={setSlot('instantEtaMinutes')} />
              <span className={form.hint}>Shown to customers placing an instant order.</span>
            </label>
          </div>

          {slotSaveError && (
            <p className={styles.errorText}>
              <AlertTriangle size={12} /> {slotSaveError}
            </p>
          )}

          <Button type="submit" disabled={!slotDirty || slotSaving} className={styles.saveBtn}>
            {slotSaving ? 'Saving…' : slotJustSaved ? <><CheckCircle2 size={15} /> Saved</> : 'Save changes'}
          </Button>
        </form>
      )}
    </div>
  );
}
